import { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

/** Renders children only if the account holds one of the permissions. */
export function Can({ permission, children, fallback = null }) {
  const { can } = useAuth();
  const list = Array.isArray(permission) ? permission : [permission];
  return can(...list) ? children : fallback;
}

/** Full-page loader. */
export function Loading({ label = 'Loading' }) {
  return (
    <div className="empty">
      <span className="spin" style={{ fontSize: 22 }}>◌</span>
      <div style={{ marginTop: 10 }}>{label}…</div>
    </div>
  );
}

export function Empty({ title = 'Nothing here yet', body = '' }) {
  return (
    <div className="empty">
      <div className="strong" style={{ fontSize: 15, color: 'var(--navy)' }}>{title}</div>
      {body ? <div style={{ marginTop: 6 }}>{body}</div> : null}
    </div>
  );
}

export function StatCard({ label, value, sub, tone = 'blue' }) {
  return (
    <div className="stat">
      <div className={`stat-bar ${tone}`} />
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {sub ? <div className="stat-sub">{sub}</div> : null}
    </div>
  );
}

export function Modal({ open, title, onClose, children, footer, wide = false }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className={`modal ${wide ? 'modal-wide' : ''}`}>
        <div className="modal-head">
          <h3>{title}</h3>
          <button className="x-btn" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </div>
  );
}

export function Pagination({ meta, onPage }) {
  if (!meta || meta.pages <= 1) return null;
  return (
    <div className="pager">
      <button className="btn btn-sm btn-ghost" disabled={meta.page <= 1} onClick={() => onPage(meta.page - 1)}>
        Previous
      </button>
      <span className="muted">Page {meta.page} of {meta.pages} · {meta.total} record(s)</span>
      <button className="btn btn-sm btn-ghost" disabled={meta.page >= meta.pages} onClick={() => onPage(meta.page + 1)}>
        Next
      </button>
    </div>
  );
}

/** Colour-codes an order status consistently everywhere it is shown. */
export function StatusBadge({ status }) {
  const tone =
    status === 'delivered' ? 'green'
    : status === 'cancelled' ? 'red'
    : ['placed', 'confirmed'].includes(status) ? 'amber'
    : 'blue';
  const label = String(status || '').replace(/-/g, ' ');
  return <span className={`badge badge-${tone}`} style={{ textTransform: 'capitalize' }}>{label}</span>;
}

export const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

export const dateTime = (d) =>
  d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-';

export const dateOnly = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
